/**
 * Add/Edit Client Page - Microsoft 365 Style
 * Client registration and management form
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Textarea,
  Field,
} from '@fluentui/react-components';
import { ArrowLeftRegular, SaveRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { SearchableDropdown } from '../../components/common';
import { ApiClient, newToast } from '../../configurations/index.config';
import { countries } from '../../configurations/countries';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  formSection: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e1dfdd',
  },
});

interface ClientCategory {
  id: string;
  name: string;
}

export const AddClientFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [clientCategories, setClientCategories] = useState<ClientCategory[]>([]);

  const [formData, setFormData] = useState({
    clientName: '',
    gender: '',
    email: '',
    address: '',
    phone: '',
    taxId: '',
    categoryId: '',
    clientType: 'individual',

    // Contact Person
    contactName: '',
    contactGender: '',
    contactPhone: '',
    contactEmail: '',
    contactRole: '',
  });

  useEffect(() => {
    fetchClientCategories();

    // Check if editing existing client
    const state = location.state as any;
    if (state?.client) {
      populateForm(state.client);
    }
  }, [location]);

  const fetchClientCategories = async () => {
    try {
      const response = await ApiClient.get('/get-client-category');
      if (response.data.status === 'success') {
        setClientCategories(response.data.data || []);
      }
    } catch (error: any) {
      newToast('error', 'Error fetching client categories');
    }
  };

  const populateForm = (client: any) => {
    setFormData({
      clientName: client.client_name || '',
      gender: client.gender || '',
      email: client.email || '',
      address: client.address || '',
      phone: client.phone || '',
      taxId: client.tax_id || '',
      categoryId: client.category_id || '',
      clientType: client.client_type || 'individual',
      contactName: client.contact_name || '',
      contactGender: client.contact_gender || '',
      contactPhone: client.contact_phone || '',
      contactEmail: client.contact_email || '',
      contactRole: client.contact_role || '',
    });
  };

  const handleSubmit = async () => {
    if (!formData.clientName || !formData.email || !formData.phone) {
      newToast('warning', 'Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: (location.state as any)?.client?.id || null,
        client_name: formData.clientName,
        gender: formData.gender,
        email: formData.email,
        address: formData.address,
        phone: formData.phone,
        tax_id: formData.taxId,
        category_id: formData.categoryId || null,
        client_type: formData.clientType,
        contact_name: formData.contactName,
        contact_gender: formData.contactGender,
        contact_phone: formData.contactPhone,
        contact_email: formData.contactEmail,
        contact_role: formData.contactRole,
      };

      const response = await ApiClient.post('/add-update-client', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Client saved successfully');
        navigate('/account/clients');
      } else {
        newToast('error', response.data?.message || 'Failed to save client');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Button
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={() => navigate('/account/clients')}
        >
          Back
        </Button>
        <Text className={styles.pageTitle}>
          {(location.state as any)?.client ? 'Edit Client' : 'Add New Client'}
        </Text>
      </div>

      <ContentCard>
        {/* Client Information */}
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Client Information</Text>

          <div className={styles.formRow}>
            <Field label="Client Name" required>
              <Input
                value={formData.clientName}
                onChange={e => setFormData({ ...formData, clientName: e.target.value })}
                placeholder="Enter client name"
              />
            </Field>

            <Field label="Client Type">
              <SearchableDropdown
                placeholder="Select type"
                options={[
                  { id: 'individual', label: 'Individual' },
                  { id: 'corporate', label: 'Corporate' },
                  { id: 'government', label: 'Government' },
                ]}
                selectedValue={formData.clientType}
                onSelect={value => setFormData({ ...formData, clientType: value })}
              />
            </Field>

            <Field label="Category">
              <SearchableDropdown
                placeholder="Search category..."
                options={[
                  { id: '', label: 'None' },
                  ...clientCategories.map(cat => ({
                    id: cat.id,
                    label: cat.name,
                  })),
                ]}
                selectedValue={formData.categoryId}
                onSelect={value => setFormData({ ...formData, categoryId: value })}
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Gender">
              <SearchableDropdown
                placeholder="Select gender"
                options={[
                  { id: 'Male', label: 'Male' },
                  { id: 'Female', label: 'Female' },
                  { id: 'N/A', label: 'N/A (Corporate)' },
                ]}
                selectedValue={formData.gender}
                onSelect={value => setFormData({ ...formData, gender: value })}
              />
            </Field>

            <Field label="Phone" required>
              <Input
                value={formData.phone}
                onChange={e => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </Field>

            <Field label="Email" required>
              <Input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Tax ID">
              <Input
                value={formData.taxId}
                onChange={e => setFormData({ ...formData, taxId: e.target.value })}
                placeholder="Enter tax ID"
              />
            </Field>

            <Field label="Address" style={{ gridColumn: 'span 2' }}>
              <Textarea
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
                placeholder="Enter address"
                rows={2}
              />
            </Field>
          </div>
        </div>

        {/* Contact Person */}
        <div className={styles.formSection}>
          <Text className={styles.sectionTitle}>Contact Person</Text>

          <div className={styles.formRow}>
            <Field label="Name">
              <Input
                value={formData.contactName}
                onChange={e => setFormData({ ...formData, contactName: e.target.value })}
                placeholder="Enter contact person name"
              />
            </Field>

            <Field label="Gender">
              <SearchableDropdown
                placeholder="Select gender"
                options={[
                  { id: 'Male', label: 'Male' },
                  { id: 'Female', label: 'Female' },
                ]}
                selectedValue={formData.contactGender}
                onSelect={value => setFormData({ ...formData, contactGender: value })}
              />
            </Field>

            <Field label="Role">
              <Input
                value={formData.contactRole}
                onChange={e => setFormData({ ...formData, contactRole: e.target.value })}
                placeholder="e.g., Manager, Director"
              />
            </Field>
          </div>

          <div className={styles.formRow}>
            <Field label="Phone">
              <Input
                value={formData.contactPhone}
                onChange={e => setFormData({ ...formData, contactPhone: e.target.value })}
                placeholder="Enter contact phone"
              />
            </Field>

            <Field label="Email">
              <Input
                type="email"
                value={formData.contactEmail}
                onChange={e => setFormData({ ...formData, contactEmail: e.target.value })}
                placeholder="Enter contact email"
              />
            </Field>
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button appearance="secondary" onClick={() => navigate('/account/clients')}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Client'}
          </Button>
        </div>
      </ContentCard>
    </div>
  );
};

export default AddClientFluentUI;
